<template>
  <load-content :loading="isReady == false">
    <div class="container">
      <div class="flex-layout">
        <div class="flex-5 mr20">
          <datatable-search
              :key="`dataTabelSearch${filterKey}`"
              name="search-articles"
              :data="searchOptions"
              :data-headline="true"
              :data-outlet="true"
              :filters="searchBarFilters"
              :countries="countries"
              :states="analysis_article_states"
              class="card search-area"
              :onSearch="onSearch"
              :onSearchSubmit="onSearchSubmit"
              :isBusy="dataSearchIsBusy"
          />
        </div>
        <div class="flex-2">
          <button
            class="btn btn-carma btn-square pull-right mt10"
            @click="shownNewArticleForm = true">
            <i class="fas fa-plus"></i>
            Add Article
          </button>
        </div>
      </div>

      <side-panel v-if="shownNewArticleForm" :onClose="closeSidePanel">
        <form-article-create
          :authors="authors"
          :project-id="projectUid"
          :user="user"
          @on-article-created="articleCreated"
        ></form-article-create>
      </side-panel>

      <side-panel v-if="shownExportForm" :onClose="closeSidePanel">
        <form-export-resource
          type="article"
          :fields="articleExportColumns"
          :ids="selectedArticleIds"
          translationPath="articles.export.label"
          :params="articleExportParams"
          :text="$t('exports.youtube_restriction')"
        />
      </side-panel>

      <datatable-bulk-actions
        v-if="selectedRows.length"
        v-model="actionTarget"
        type="article"
        :rows="selectedRows"
        :total="articlesTotal"
        :actions="bulkActions"
        translationsPath="articles.bulk_actions"
        @cancel="closeModalAction"
        @input="onBulkOption"
        @group="onBulk('group')"
        @export="exportArticles"
        @open="onBulkOpen('open')"
        @edit="onBulk('edit')"
        @remove="onBulk('remove')"
        @delete="onBulk('delete')"
        @aiCode="onBulk('ai_code')"
      />

      <div>
        <div class="table-top">
          <div class="filters-articles-list">
          <articles-filter-date
            :title="$t('articles.form.filter_date.title')"
            :from.sync="filters.dateFrom"
            :to.sync="filters.dateTo"
            :type.sync="filters.dateType"
            :onChange="onChangeDateFilters"
            :disabled="fetchingArticles"
            @updateRange="updateDateRangeFilter"
            class="mr15"
            :showDateTimePicker="isProjectPage"
            :formatDate="(value) => formatDateParam(value, isProjectPage)"
          />
          <articles-filter-group
            v-if="mainRouteList && groupOptions.length > 0"
            :title="$t('articles.form.filter_group.title')"
            :groups="groupOptions"
            :value="filters.groupId"
            :onChange="onChangeGroupFilter"
          />
          </div>
          <div v-show="mainRouteList && user.code_admin" class="toggle-unfiltered">
            <input-toggle
              :checked="filters.unfiltered == 1"
              :onChange="onChangeUnfiltered"
              :disabled="!unfilteredEnabled"
              data-tip
              v-tooltip="!unfilteredEnabled && $t('articles.warning.unfiltered_disabled')"
            />
            <span>{{$t("articles.include_unfiltered")}}</span>
          </div>
        </div>
      </div>

      <div v-if="title">
        <h4 class="text-gray">{{title}}</h4>
      </div>

      <div class="card">
        <div class="articles-wrapper pc-content-datatable">
          <datatable
            ref="table"
            :key="`data-table-articles${dataTableArticleKey}`"
            :data="tableData"
            :loading="loadingTableContent"
            :no-results="$t('datatable.empty_articles')"
            :updating-row="updatingRowId"
            :total-records="articlesTotal"
            :over-total-limit="overTotalLimit"
            :max-to-list="maxToList"
            :save-preferences="true"
            code="articles_table"
            @onReachEnd="onReachEnd"
          />
          <div class="loading-next-page" v-if="loadingNextPage"><loader /></div>
        </div>
      </div>
    </div>
    <side-panel v-if="articlePreviewOpen" :onClose="closeSidePanel" class="article-sidepanel">
      <template
        slot="topContentLeft"
      >
        <div class="d-flex align-items-center">
          <span>{{$t('articles.status')}}:</span>
          <InputSelect
            v-model="articlePreviewContent.analysis_state"
            :searchable="true"
            :options="articleStatesOptions"
            :allowEmpty="false"
            :placeholder="$t('project.form.select_state')"
            :onChange="previewUpdateStatus"
            :white-background="true"
            class="preview-select-status"
          />
        </div>
      </template>
      <template
        v-if="showTopContent"
        slot="topContent"
      >
        <span class="mb20 top-preview">

          <a
            :href="viewEditorLink"
            v-tooltip="$t(!viewOnEditorEnabled ? 'project.message.unassigned_disable_open_editor' : '')"
            :class="{ 'cursor-default': !viewOnEditorEnabled, 'disabled-keep-event' : !viewOnEditorEnabled}"
            class="editor-ui-link"
            @click.prevent="openOnEditor"
          >
            <i class="fas fa-external-link-alt"></i> View on editor
          </a>
          <a
              :href="insightUrl(articlePreviewContent.coding_uid)"
              :class="{ disabled: !articlePreviewContent.article_synced }"
              class="editor-ui-link ml20"
              target="_blank"
          >
            <i class="fas fa-external-link-alt"></i> View on Insight
          </a>
        </span>
      </template>
      <load-content :loading="articlePreviewIsReady === false">
        <template v-if="articlePreviewIsReady === true">
          <ArrowsNav
              :show-left="!!(sidebarPagination.previousArticle && sidebarPagination.previousArticle.id)"
              :show-right="!!((sidebarPagination.nextArticle && sidebarPagination.nextArticle.id) || this.requestPagination.currentPage < this.requestPagination.totalPages)"
              @on-click-left="openArticlePreview(sidebarPagination.previousArticle)"
              @on-click-right="fetchNextArticle(sidebarPagination.nextArticle)"
          />

          <h3>
            <a v-if="articlePreviewContent.url" :href="articlePreviewContent.url" target="_blank">{{articlePreviewContent.headline}}</a>
            <span v-else>{{articlePreviewContent.headline}}</span>
          </h3>
          <!-- assets = {{ assets }} -->
          <div class="mb20" v-if="assets.length">
            <template>
              <thumbnail
                v-for="asset in assets"
                :key="asset.id"
                :asset="asset"
              />
            </template>
          </div>
          <div class="summary" v-if="articleSummary.length > 0">
            <article-body-renderer v-for="(paragraph , index) in articleSummary"
              :key="index"
              :paragraph="paragraph"
              :parKey="index"
              :selectedClassName="''"
              :noOfSelectedSentences="0"
            />
            <input-button
              v-if="summary.length > firstParagraphs.length"
              :label_button="this.showMoreArticle === true ? 'show less' : 'show more'"
              :onClick="() => toggleShowMoreArticle()"
              :class="'btn btn-default btn-sm btn-quarter-width'"
            />
          </div>
          <template>
            <div class="mention-wrapper" v-if="mentions.length > 0">
              <mention :showScoringSystem="showScoringSystem" :primary_tag="mention" :allowDelete="false" v-for="(mention , index) in mentions" :key="index"/>
            </div>
          </template>
        </template>
        <template v-if="aiCoding.length">
          <div class="mt20 explanation">
            <div>
              <h4 class="mb-0">{{$t('articles.ai_explanation')}}</h4>
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="text-orange mb-0">{{ aiCoding[aiCodingIndex].name }} - {{ aiCoding[aiCodingIndex].model }}</h5>
                <div class="btn-container d-flex justify-content-end">
                  <button class="prev-btn btn btn-carma w-auto mr-2 mt-0" :class="{ 'disabled': aiCodingIndex === 0 || !aiCoding[aiCodingIndex].explanation}" @click="handleAiCodingPrev" :disabled="aiCodingIndex === 0 || !aiCoding[aiCodingIndex].explanation"><i class="fa fa-chevron-circle-left fa-xl" aria-hidden="true"></i></button>
                  <button class="next-btn btn btn-carma w-auto mt-0" :class="{ 'disabled': aiCodingIndex === aiCoding.length-1 || !aiCoding[aiCodingIndex].explanation}" @click="handleAiCodingNext" :disabled="aiCodingIndex === aiCoding.length-1 || !aiCoding[aiCodingIndex].explanation"><i class="fa fa-chevron-circle-right fa-xl" aria-hidden="true"></i></button>
                </div>
              </div>
            </div>
            <div>{{ aiCoding[aiCodingIndex].explanation }}</div>
          </div>
        </template>
      </load-content>
    </side-panel>

    <side-panel v-if="bulkGroupOpen" :onClose="closeSidePanel">
      <template>
        <form-article-to-group
          :articles="selectedRows"
          :groups="groupOptions"
          :onSubmit="onSubmitArticlesToGroup"
          :onSubmitTag="onNewTagCreateGroup"
          :loading="sidePanelIsBusy"
        />
      </template>
    </side-panel>

    <side-panel v-if="bulkEditOpen" :onClose="closeSidePanel">
      <template>
        <load-content :loading="sidePanelIsBusy">
          <form-edit-articles
            v-if="!showErrorMessage"
            :articles="selectedRows"
            :coders="coderOptions"
            :editCoderEnabled="editCoderEnabled"
            :outlets="outlets"
            :article_states="article_states"
            :organizations="organizations"
            :categories_tags="categories"
            :articles_relationship_lines="articles_relationship_lines"
            :articles_relationship_lines_busy="articles_relationship_lines_busy"
            :onSubmitDeleteMention="deleteMention"
            :onSubmit="onSubmitEditArticles"
            :action-target="actionTarget"
          />
          <div v-else class="mt20">
            <form class="form form--sidepanel">

              <h3>
                <span class="far fa-exclamation-circle"></span> {{ $t('articles.warning.title') }}
              </h3>
              
              <p class="pt20 mb20">{{ errorMessage }}</p>

              <ul v-if="errorArticles.length" class="list list-inside pl10">
                <li v-for="(article, i) in errorArticles" :key="i">
                  {{ article.id }} - {{ article.headline }}
                </li>
              </ul>

              <ul v-if="errorCategories.length" class="font-bold">
                <li v-for="item in errorCategories" :key="item.id">
                  {{ item.name}} (ID: {{ item.id }})
                </li>
              </ul>

              <p v-if="errorMessageAfterList" v-html="errorMessageAfterList" class="mt20"></p>

              <p v-if="showConfirmForceUpdate" class="mt20 pb20">
                {{ $t('articles.warning.confirm_proceed') }}
              </p>

              <div class="form-group form-footer">
                <button
                  class="btn btn-default w-auto btn-sm mt10"
                  @click="onCancelConfirmForceUpdate">
                  {{ $t('form.button.cancel') }}
                </button>
                <button
                  v-if="showConfirmForceUpdate"
                  class="btn btn-carma w-auto btn-sm mt10"
                  @click="onSubmitEditArticlesConfirmForce">
                  {{ $t('form.button.confirm') }}
                </button>
              </div>
            </form>
          </div>
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="bulkDeleteOpen" :onClose="closeSidePanel">
      <template>
        <load-content :loading="sidePanelIsBusy">
          <confirm-delete
            :label="selectedLabel"
            :bulk="true"
            :onDelete="() => onSubmitArticlesToDelete(selectedRows)"
            :onCancel="closeSidePanel"
            :requiresPrompt="!isGroupArticlePage"
            authAction="delete_articles"
          />
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="bulkRemoveFromGroupOpen" :onClose="closeSidePanel">
      <template>
        <load-content :loading="sidePanelIsBusy">
          <form-remove-from-group
            :group="group"
            @remove="() => onSubmitArticlesToDelete(selectedRows)"
            @cancel="closeSidePanel"
          />
        </load-content>
      </template>
    </side-panel>

    <side-panel v-if="bulkAICode" :onClose="closeSidePanel">
      <load-content :loading="sidePanelIsBusy">
        <form-ai-code-articles
          @submit="onAICodeArticles"
        />
      </load-content>
    </side-panel>
    
  </load-content>
</template>

<script>
import Axios from "axios";
import { mapState, mapGetters, mapActions } from "vuex";
import Moment from "moment";
import _ from "lodash";
import i18n from '@/i18n';
import { displayDate } from "@/helpers/date";
import { BULK_UPDATE, ARTICLE_STATUS, ARTICLES, MAX_ARTICLES_TO_LIST, ARTICLES_EXPORT, USER_PERMISSIONS, FIELD_TYPE, SCORING_METHODS, SENTIMENT } from "@/config/const";
import { API_HOST } from "@/config/app-const";
import { insightExternalUrl } from "@/helpers/articles";
import { parseQuery } from "@/helpers/parseQuery"
import { mapParamsFromURL } from "@/helpers/mapParamsFromURL"
import LoadContent from "@/components/load-content";
import Loader from "@/components/loader";
import ArrowsNav from "@/components/arrows-nav/";
import Datatable from "@/components/datatable";
import CoderPicker from "@/components/coder-picker";
import CodingHeadline from "@/components/coding-headline";
import SidePanel from "@/components/side-panel";
import ConfirmDelete from "@/components/confirm-delete";
import DatatableSearch from "@/components/datatable-search";
import FormArticleToGroup from "@/components/coding-admin/form-article-to-group";
import FormRemoveFromGroup from "@/components/coding-admin/form-remove-from-group";
import FormArticleCreate from "@/components/coding-admin/form-article-create";
import FormEditArticles from "@/components/coding-admin/form-edit-articles";
import FormAICodeArticles from '@/components/coding-admin/form-ai-code-articles';
import InputToggle from "@/components/form-components/input-toggle";
import ArticlesFilterDate from "@/components/coding-admin/articles-filter-date";
import ArticlesFilterGroup from "@/components/coding-admin/articles-filter-group";
import InputButton from "@/components/form-components/input-button";
import Thumbnail from "@/components/coding-admin/articles-thumbnail";
import OutletsPicker from "@/components/outlets-picker";
import { getUrlParams } from '@/helpers/pagination';
import Mention from "@/components/coding-admin/mention";
import ArticleBodyRenderer from "@/components/coding-admin/article-body";
import InputSelect from '@/components/form-components/input-select';
import FormExportResource from '@/components/coding-admin/form-export-resource';
import DatatableBulkActions from '@/components/datatable-bulk-actions';
import { relevancyMappingDefaultLimit } from '@/config/relevancy';
import { FIXED_TABLE_COLUMNS } from '@/config/const';

const dateFormat = "YYYY-MM-DD";

export default {

  async created() {
    const query = this.checkURLParams(this.$route.query);

    this.getCoders({ projectId: this.$route.params.project_uid }).then(r => {
      this.isReadyCoders = true;
    });

    this.getArticleStatus({ projectId: this.$route.params.project_uid });
    this.$router.replace({name: this.$route.name, query: {...query, page: 1}}).catch(err => err)
    query.page = 1; // Ensure page is set to 1
    this.getRelevancy({projectId: this.projectUid, type: 'Coding'});

    if(this.isTagArticlePage){
      this.getTag(this.$route.params.tag_id);
    }

    // If we don't await this then we don't know the name of the page author and will get an error on search filters.
    if (this.isAuthorArticlePage) {
      await this.getAuthors({ projectId: this.projectUid, pagination: {}, filters: { id: [{ value: this.$route.params.author_id }] } });
    }
    
    // Map all url params on query.filters obj
    if(Object.keys(query).length) {
      const paginationKeys = ['page', {'per_page' : 'perPage'}, 'order', 'sort']
      const pagination = mapParamsFromURL(query, paginationKeys)
      this.pagination = Object.assign({}, this.pagination, pagination)

      const filtersKeys = [
        {"group_id" : "groupId"},
        "unfiltered",
        ["coder_id"],
        ["country_code"],
        ["user_country_code"],
        ["keyword"],
        ["text_in_outlet"],
        ["text_in_headline"],
        ["outlet_name"],
        ["media_type"],
        ["status_id"],
        ["outlet_tier"],
        ["article_id"],
        ["tag_id"],
        ["insight_tag_id"],
        ["languages"],
        ["coding_uid"],
        ["insight_url"],
        ["source_url"],
        ["relevancy"],
        ["author_id"],
        ["social"]
      ]

      const filters = mapParamsFromURL(query, filtersKeys)
      Object.keys(filters).forEach(key => {
        const splitKey = key.split('[')
        if(splitKey.length > 1) {
          const filterKey = splitKey[0]
          const objKey = splitKey[2].slice(0, -1)
          const objvalue = filters[key]
          const index = splitKey[1].slice(0, -1)

          const existingFilter = this.filters[filterKey].find(filter => filter.id === index)

          if(existingFilter) {
            this.$set(existingFilter, [objKey], objvalue)
          } else {
            this.filters[filterKey].push({
              [objKey]: objvalue,
              id: index
            })
          }
        } else {
          this.$set(this.filters, [key], filters[key])
        }
      })

      Object.keys(this.filters).forEach(key => {
        if(this.filters[key] && Array.isArray(this.filters[key]) && this.filters[key].length) {
          this.filters[key].forEach((filter, index) => {
            filter.index = parseInt(filter.index) || index
            this.$delete(filter, 'id')
          })
        }
      })

      this.processRouteParams();
      this.setFilterDate();
    }

    let filterParams = this.sanitiFilterParams(_.cloneDeep(this.filters));
    this.fetch(filterParams);
    this.scroll();
  },

  components: {
    InputSelect,
    Datatable,
    LoadContent,
    SidePanel,
    ConfirmDelete,
    DatatableSearch,
    FormArticleToGroup,
    FormRemoveFromGroup,
    FormArticleCreate,
    FormEditArticles,
    'form-ai-code-articles': FormAICodeArticles,
    Loader,
    InputToggle,
    ArticlesFilterDate,
    InputButton,
    Thumbnail,
    Mention,
    ArrowsNav,
    ArticlesFilterGroup,
    ArticleBodyRenderer,
    FormExportResource,
    DatatableBulkActions
  },

  data: function() {
    const isProjectPage = this.$route.name === 'project';
    const isTagArticlesPage = this.$route.name === 'tag-articles' && this.$route.params.tag_id;

    const baseFilters = {
      dateFrom: isTagArticlesPage ? null : this.formatDateParam(Moment.utc().startOf('day'), isProjectPage),
      dateTo: isTagArticlesPage ? null : this.formatDateParam(Moment.utc().endOf('day'), isProjectPage),
      dateType: ARTICLES.FILTERS.PUBLISHED_AT,
      unfiltered: 0,
      coder_id: [],
      country_code: [],
      keyword: [],
      text_in_outlet: [],
      text_in_headline: [],
      outletId: [],
      outlet_name: [],
      media_type: [],
      status_id: [],
      outlet_tier: [],
      article_id: [],
      groupId: "",
      tag_id: [],
      user_country_code: [],
      insight_tag_id: [],
      languages: [],
      source_url: [],
      insight_url: [],
      coding_uid: [],
      relevancy: [],
      author_id: [],
      social: []
    }

    return {
      isProjectPage,
      error: null,
      showErrorMessage: false,
      newModel: null,
      analysisStateId: null,
      articleStates: [],
      queryInitialLoad: false,
      dataTableArticleKey: 1,
      actionTarget: 'selected',
      shownNewArticleForm: false,
      shownExportForm: false,
      showMoreArticle: false,
      isReadyArticles: false,
      isReadyCoders: false,
      counter: 0,
      fetchingArticles: false,
      fetchingBulkUpdate: false,
      projectUid: this.$route.params.project_uid,
      selectedTag: null,
      tableData: {},
      articlePreviewOpen: false,
      articlePreviewIsReady: false,
      articlePreviewContent: {},
      sidebarPagination: {
        previousArticle: {},
        currentArticle: {},
        nextArticle: {},
      },
      // searchData: {},
      findResultsRequest: null,
      // submittedSearchParams: [],
      dataSearchIsBusy: false,
      selectedRows: [],
      pagination: {
        page: 1,
        perPage: 20,
        order: ARTICLES.FILTERS.PUBLISHED_AT,
        sort: "desc"
      },
      filters: baseFilters,
      searchBarFilters: baseFilters,
      tableHeaderFilters: [ 'coder_id', 'status_id', 'outlet_tier', 'country_code', 'outlet_id'],
      bulkGroupOpen: false,
      bulkEditOpen: false,
      bulkDeleteOpen: false,
      bulkRemoveFromGroupOpen: false,
      bulkAICode: false,
      scrolledToBottom: false,
      updatingRowId: [],
      first_par_max_characters: 400,
      filterKey: 0,
      isDateTimeUpdated: false,
      aiCodingIndex: 0
    };
  },

  computed: {
    ...mapGetters('articles', [
      'articlesTotal',
      'errors',
      'overTotalLimit',
    ]),

    pageOrigin () {
      return this.$route.name;
    },

    isReady() {
      return this.isReadyArticles && this.isReadyCoders && this.project !== null && this.project !== undefined;
    },

    articleExportColumns () {
      let columns = ARTICLES_EXPORT.COLUMNS;
      if (this.socialsDataEnabled) {
        columns = columns.filter(c => c !== 'authors');
        columns = [...columns, ...ARTICLES_EXPORT.SOCIAL_COLUMNS];
      }
      columns = [...columns, ...ARTICLES_EXPORT.NEW_COLUMNS]
      return columns;
    },

    unfilteredEnabled () {
      return this.filters.dateFrom && this.filters.dateTo;
    },

    errorMessage() {
      if (this.error && this.error.message)
        return this.$t(`articles.warning.messages.${this.error.message}`);

      return '';
    },
    errorCategories() {
      if (_.get(this.error, 'data.categories') && this.error.data.categories.length)
        return this.error.data.categories;

      return '';
    },
    errorMessageAfterList() {
      if (this.error && this.error.message ===  ARTICLES.ERRORS.CODE.MISSING_CATEGORIES)
        return this.$t(`articles.warning.after_list.${this.error.message}`);

      return '';
    },
    showConfirmForceUpdate() {
      return (this.error && [ARTICLES.ERRORS.CODE.CODED_ARTICLES_SELECTED].indexOf(this.error.message) > -1);
    },
    selectedArticleIds() {
      return this.selectedRows.map(r=>r.id);
    },
    maxToList() {
      return MAX_ARTICLES_TO_LIST;
    },
    mainRouteList() {
      return this.$route.name === 'project';
    },
    errorArticles() {
      if (
        _.get(this.errors, 'data.articles') &&
        this.errors.data.articles.length
      )
        return this.errors.data.articles;

      return [];
    },
    articleStatesOptions() {
      return this.articleStates.map((status) => {
        const isDisabled =
          _.get(this.articlePreviewContent, "assigned_to.id") &&
          (status.id === ARTICLE_STATUS.PUBLISHED_QC ||
            status.id === ARTICLE_STATUS.PUBLISHED_NOT_QC);

        return {
          value: status.id,
          label: status.status,
          disabled: isDisabled,
        };
      });
    },
    viewOnEditorEnabled() {
      if (this.articlePreviewContent.assigned_to && this.articlePreviewContent.assigned_to.id)
        return true;

      return false;
    },
    viewEditorLink() {
      const filters = _.cloneDeep(this.filters);
      filters.dateFrom = this.formatDateParam(filters.dateFrom);
      filters.dateTo = this.formatDateParam(filters.dateTo);

      const params = getUrlParams({
        projectId: this.projectUid,
        ...this.pagination,
        ...filters,
        origin: this.pageOrigin
      });

      return `/analysis/editor/${this.projectUid}/${this.articlePreviewContent.editor_article_id}?${params}`;
    },
    actionTargetFiltered() {
      return BULK_UPDATE.ACTION_TARGET.FILTERED;
    },
    actionTargetSelected() {
      return BULK_UPDATE.ACTION_TARGET.SELECTED;
    },
    showTopContent() {
      return (
        (
          this.user.code_admin == true ||
          (
            this.user.permissions &&
            this.user.permissions.view_coding_ui == true
          )
        ) &&
        this.articlePreviewContent &&
        this.articlePreviewContent.editor_article_id &&
        this.articlePreviewContent.country_id
      );
    },
    actionToAllRecordsStatus() {
      return this.actionTarget === this.actionTargetFiltered ||
      this.articlesTotal === this.selectedRows.length;
    },
    actionToAllRecordsData() {
      return {
        status: this.actionToAllRecordsStatus,
        filters: this.filters,
        total: this.articlesTotal
      }
    },
    initialLoading() {
      return this.isBusy === true && this.pagination.page === 1 && this.fetchingArticles === false;
    },
    loadingNextPage() {
      return this.fetchingArticles === true && this.pagination.page !== 1;
    },
    loadingTableContent() {
      return this.fetchingArticles && this.pagination.page === 1;
    },
    selectedLabel() {
      if(this.actionToAllRecordsStatus)
        return this.$tc("messages.articles.delete_label", this.articlesTotal, { count: this.articlesTotal });
      return `${this.selectedRows.length} ${
        this.selectedRows.length == 1 ? "article" : "articles"
      } selected`;
    },
    editGroupEnabled: function() {
      return this.user.code_admin == true || this.user.permissions.assign_articles_to_group == true;
    },
    groupOptions: function() {
      return this.groups.map(g => ({ value: g.id, label: g.name }));
    },
    summary: function(){
      return !_.isEmpty(this.articlePreviewContent.summary) ? this.articlePreviewContent.summary : [];
    },
    aiCoding: function(){
      return this.articlePreviewContent.ai_response || [];
    },
    firstParagraphs: function() {
      const max_characters = this.first_par_max_characters || 400;
      let firstParagraphs = [];

      if(!_.isEmpty(this.summary)){
        let length = 0;
        _.forEach(this.summary, function(p){
            if (length === max_characters) {
              return null;
            }
            else if (length + p.content.length < max_characters) {
              length += p.content.length
              firstParagraphs.push(p);
            }
            else {
              let diff = max_characters - length;
              let new_p = Object.assign({}, p);
              length += diff;
              new_p.content = new_p.content.substring(0, diff);
              if (length === max_characters) new_p.content = new_p.content + " ...";
              firstParagraphs.push(new_p);
            }
        });
      }

      return firstParagraphs
    },
    articleSummary: function(){
        return this.showMoreArticle === true ? this.summary : this.firstParagraphs;
    },
    ...mapState({
      preferences: state => state.user.preferences,
      user: state => state.user.user.data,
      project: state => state.projects.project.data,
      articles: state => state.articles.articles.data ? state.articles.articles.data.data.articles : [],
      article: state => state.articles.article.data || {},
      requestPagination: state => state.articles.articles.data ? state.articles.articles.data.pagination : {},
      media_types: state => state.articles.articles.data ? state.articles.articles.data.data.media_types : [],
      outlets: state => state.articles.articles.data ? state.articles.articles.data.data.outlets : [],
      authors: state => state.admin.authors.authors.data || [],
      outletTiers: state => state.articles.articles.data ? state.articles.articles.data.data.outlet_tiers : [],
      countries: state => state.articles.articles.data ? state.articles.articles.data.data.countries : [],
      analysis_article_states: state => state.articles.articles.data ? state.articles.articles.data.data.analysis_article_states : [],
      coders: state => state.articles.coders ? state.articles.coders.data : [],
      codersIsBusy: state => state.articles.coders.isBusy,
      groups: state  => state.articles.articles.data ? state.articles.articles.data.data.groups : [],
      isBusy: state => state.articles.articles.isBusy,
      article_states: state => state.articles.article_status ? state.articles.article_status.data : [],
      find_results: state => state.articles.find_results.data ? state.articles.find_results.data : {},
      sidePanelIsBusy: state => state.articles.sidePanel.isBusy,
      articleIsUpdating: state => state.articles.article.updating,
      articleGroup: state => state.articleGroup.articleGroup.data || {},
      mentions: state => (!_.isEmpty(_.get(state.articles.articlesRelationshipLines, 'data[0].mentions'))) ? state.articles.articlesRelationshipLines.data[0].mentions : [],
      //mentions: state => state.articles.mentions.data ? state.articles.mentions.data : [],
      assets: state => state.articles.assets.data && state.articles.assets.data.data ? state.articles.assets.data.data : [],
      organizations: state =>
        state.articles.organizations?.data?.data?.data
          ? state.articles.organizations.data.data.data
          : [],
      categories: state =>
        state.articles.categories.data ? state.articles.categories.data : [],
      // tags: state => state.projectSetup.projectSetup.data ? state.projectSetup.projectSetup.data.tags : [],
      articles_relationship_lines: state => state.articles.articlesRelationshipLines.data ? state.articles.articlesRelationshipLines.data : [],
      articles_relationship_lines_busy: state => state.articles.articlesRelationshipLines.isBusy,
      showScoringSystem: state => !_.isEmpty(state.articles.settings.data) && state.articles.settings.data.scoring != SCORING_METHODS.NONE,
      relevancy: state => state.relevancy.relevancy?.data?.data || [],
    }),
    editCoderEnabled: function() {
      return this.user.code_admin == true || (this.user.permissions.assign_articles != null && this.user.permissions.assign_articles != USER_PERMISSIONS.ASSIGN_ARTICLES.NO)
    },
    coderOptions: function() {
      const { user } = this;

      let coders = this.coders ? [...this.coders] : [];

      if (this.user.permissions.assign_articles == USER_PERMISSIONS.ASSIGN_ARTICLES.SELF) {
        coders = coders.filter(c => c.user_id == user.id);
      }
      coders.unshift({name: 'Unassign', id: 0});

      return coders;
    },
    checkSidepanelOpen: function(){
      return  this.articlePreviewOpen === true || this.bulkGroupOpen ===  true || this.bulkEditOpen === true || this.bulkRemoveFromGroupOpen || this.bulkDeleteOpen === true || this.showMoreArticle === true || this.bulkAICode === true;
    },
    tableHeaderCoder() { return (this.filters && this.filters.coder_id && this.filters.coder_id.length) ? this.filters.coder_id[0] : null },
    tableHeaderStatus() { return (this.filters && this.filters.status_id && this.filters.status_id.length) ?this.filters.status_id[0] : null },
    tableHeaderTier() { return (this.filters && this.filters.outlet_tier && this.filters.outlet_tier.length) ? this.filters.outlet_tier[0] : null },
    tableHeaderOutlet() { return (this.filters && this.filters.outlet_name && this.filters.outlet_name.length) ? this.filters.outlet_name[0] : null },

    dataTableData: function(){
      const {
          articles,
          media_types,
          outlets,
          countries,
          coders,
          article_states,
          user,
          coderOptions,
          outletTiers,
          preferences
        } = this;

        return {
          articles,
          media_types,
          outlets,
          countries,
          coders,
          outletTiers,
          article_states,
          coderOptions,
          user,
          preferences
        }
    },
    isCoderArticlePage() { return this.$route.params.coder_id != undefined },
    isAuthorArticlePage() { return this.$route.params.author_id != undefined },
    isGroupArticlePage() { return this.$route.params.group_id != undefined },
    isTagArticlePage() { return this.$route.params.tag_id != undefined },
    isSourceArticlePage() { return this.$route.params.source_id != undefined },
    isOrganizationArticlePage() {return this.$route.params.organization_id != undefined },

    categoryId() {
      if (!this.$route.params.tag_id) return null;

      return parseInt(this.$route.params.tag_id, 0)
    },

    category() {
      if (!this.isTagArticlePage || !this.categories) return null;
    
      return this.categories.find(cat => this.tags.find(tag => tag.id === this.categoryId));
    },

    group() {
      return this.isGroupArticlePage && this.groups && this.groups.find(group => group.id === parseInt(this.$route.params.group_id));
    },

    title() {
      let title = null
      if(this.isAuthorArticlePage && this.authors) {
        const author = this.authors.find(author => author.id === parseInt(this.$route.params.author_id))
        if(author) title = `Author: ${author.first_name}${author.last_name !== null ? ' ' + author.last_name : ''}`;
      }
      if(this.isGroupArticlePage && this.groups) {
        const group = this.groups.find(group => group.id === parseInt(this.$route.params.group_id))
        if(group) title = `Group: ${group.name}`
      }
      if(this.isCoderArticlePage && this.coders) {
        const coder = this.coders.find(coder => coder.id === parseInt(this.$route.params.coder_id))
        if(coder) title = `Coder: ${coder.name}`
      }
      if(this.isTagArticlePage && this.categories) {
        if (this.selectedTag) title = `Tag: ${this.selectedTagName}`
      }
      if(this.isOrganizationArticlePage && this.categories.length > 0) {
        const tag = this.categories?.find(cat => cat?.tags?.find(tag => tag.id === parseInt(this.$route.params.organization_id)))
        if(tag) title = `Organization: ${tag.name}`
      }
      return title
    },

    selectedTagName() {
      if (!this.selectedTag) return '';

      return this.selectedTag.name;
    },

    searchOptions() {
      const options = Object.assign({}, this.find_results);
      if (this.socialsDataEnabled) {
        options.user_countries = options.countries;
      }
      if (options.media_subtypes) {
        options.media_subtypes = options.media_subtypes.map(m => {
          const label = i18n.t(`articles.media_types.${(m.label || '').replace(/-/ig, '_')}`);
          return { value: m.label, label: label };
        });
      }

      if (options.insight_url) {
        options.insight_url = options.insight_url.map(opt => {
          const label = insightExternalUrl(opt.value);
          return { value: opt.value, label: label };
        })
      }
      return options
    },

    articleExportParams() {
      const params = { unfiltered: this.filters.unfiltered }
      if (this.actionTarget === BULK_UPDATE.ACTION_TARGET.FILTERED) {
        params.filters = this.filters;
      }
      return params;
    },

    bulkActions () {
      const actions = [
        {
          type: 'group',
          enabled: this.editGroupEnabled,
          icon: 'fas fa-layer-group'
        },
        {
          type: 'export',
          enabled: this.editGroupEnabled,
          icon: 'fas fa-download'
        },
        {
          type: 'open',
          enabled: this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-external-link-square-alt'
        },
        {
          type: 'edit',
          enabled: this.bulkEditArticlesEnabled,
          icon: 'fas fa-edit'
        }
      ];

      if (this.autoCoderEnabled) {
        actions.push({
          type: 'aiCode',
          icon: 'fas fa-tags'
        });
      }

      if (this.isGroupArticlePage) {
        actions.push({
          type: 'remove',
          icon: 'fas fa-folder-minus'
        });
      } else {
        actions.push({
          type: 'delete',
          class: 'text-red',
          enabled: this.bulkDeleteArticlesEnabled,
          icon: 'fas fa-trash'
        });
      }

      return actions;
    },

    bulkDeleteArticlesEnabled () {
      const permissions = this.user.permissions || {};
      return this.user.code_admin || ['yes', 'assigned_only'].indexOf(permissions.bulk_delete_articles) > -1;
    },

    bulkEditArticlesEnabled () {
      const permissions = this.user.permissions || {};
      return this.user.code_admin || ['yes', 'assigned_only'].indexOf(permissions.bulk_update) > -1;
    },

    socialsDataEnabled () {
      return this.project?.enable_socials_data === true;
    },

    autoCoderEnabled () {
      return this.project?.enable_auto_coder === true;
    },

    isCarmaUser(){
      const carmaDomainPattern = /@carma\.com/;
      return this.user.email.match(carmaDomainPattern)
    },
    socialRelevancyEnabled () {
      return this.project?.enable_social_relevancy === true;
    },
    relevancyEnabled() {
      return this.isCarmaUser || this.project?.enable_relevancy === true;
    }
  },

  watch: {
    'filters.dateType'(){
      this.onChangeDateFilters();
    },
    'filters.dateFrom'(){
      this.onChangeDateFilters();
    },
    'filters.dateTo'(){
      if (!this.isProjectPage || this.isDateTimeUpdated) {
        this.isDateTimeUpdated = false;
        this.onChangeDateFilters();
      }
    },
    filters: {
      deep: true,
      handler(value) {
        this.searchBarFilters = _.cloneDeep(value);
      }
    },
    articles: {
      handler(value){
        this.counter++;
      },
    },
    selectedTagName() {
      this.processRouteParams();
    },
    articleIsUpdating: {
      handler(value){
        if(!Array.isArray(this.updatingRowId) && !value){
          this.updatingRowId = null
        } 
      }
    },
    dataTableData: {
      handler(value){
        const {
          articles,
          media_types,
          outlets,
          countries,
          coders,
          article_states,
          user,
          coderOptions,
          outletTiers,
          preferences
        } = value;

        this.articleStates = article_states;
        const body = articles;
        let column;
        const self = this;

        let tableColumns = [];
        let allowedColumns = ['article_id', 'headline', 'date_created', 'date_published', 'outlet', 'language', 'assigned_to', 'assigned_by', 'status', 'due_date', 'fav_score', 'outlet_tier', 'authors'];
        if (this.project?.enable_socials_data) allowedColumns.push('summary');
        if (this.relevancyEnabled) allowedColumns.push('relevancy');
        const bulkActionsEnabled = this.bulkEditArticlesEnabled || this.bulkDeleteArticlesEnabled;
        let editCodedArticlesAllowed = true;
        let resizable = true;

        if (user.code_admin == false) {
          allowedColumns = user.permissions != null && user.permissions.article_screen_data != null ? user.permissions.article_screen_data : [];
          editCodedArticlesAllowed = user.permissions != null && (user.permissions.edit_coded_articles == 'yes' || user.permissions.edit_coded_articles == 'assigned_only');
        }

        if (allowedColumns.includes('article_id')) {
          tableColumns.push({
            key: 'id',
            orderable: true,
            label: i18n.t('articles.id'),
          })
        }

        if (allowedColumns.includes('headline')) {
          let width = _.get(preferences, `articles_table_column_size.widths[${allowedColumns.indexOf('headline')}]`) || 350;
          tableColumns.push({
            key: 'headline',
            label: i18n.t('articles.headline'),
            orderable: true,
            cellStyle: {
              "max-width": `${width}px`
            },
            render: (row) => {
              return (
                <CodingHeadline
                  id={row.id}
                  headline={row.headline}
                  isButton={true}
                  nativeOnClick={() => this.openArticlePreview(row)}
                />
              )
            }
          });
        }

        if (allowedColumns.includes('assigned_to') && coders && coderOptions) {
          column = {
            key: 'assigned_to',
            label: i18n.t('articles.assigned_to'),
            orderable: true,
            render: (row) => {
              const options = this.coderOptions;
              const coder = options.find(c => c.id && c.id == row.assigned_to.id) || {};
              const allowEmpty =
                row.analysis_state !== ARTICLE_STATUS.PUBLISHED_QC &&
                row.analysis_state !== ARTICLE_STATUS.PUBLISHED_NOT_QC;

              const isDisabled =
                row.analysis_state === ARTICLE_STATUS.PUBLISHED_QC ||
                row.analysis_state === ARTICLE_STATUS.PUBLISHED_NOT_QC;

              return (
                <CoderPicker
                  row={row}
                  coder={coder}
                  options={options}
                  readonly={!this.editCoderEnabled}
                  change={this.onChangeCoder}
                  isDisabled={isDisabled}
                  isDisabledMessage={this.$t('project.message.published_disable_change_coder')}
                  loading={this.codersIsBusy}
                />
              )
            },
            filter: {
              title: i18n.t('articles.form.coder.filter'),
              name: 'assigned_to.id',
              placeholder: i18n.t('articles.form.search'),
              options: coders.map(coder => ({ value: coder.id, label: coder.name, operator: 'and' })),
              onFilter: (option) => self.onFilterTableHeader('coder_id', option),
              onClearHeader: (value) => self.clearHeader ('coder_id', value),
              applied: self.tableHeaderCoder,
            }
          }

          tableColumns.push(column);
        }

        tableColumns.push({
          key: 'media_subtype',
          label: i18n.t('articles.media_type'),
          orderable: true,
          render: (row) => {
            return <span>{i18n.t(`articles.media_types.${(row.media_subtype || '').replace(/-/ig, '_')}`)}</span>
          }
        })

        if (allowedColumns.includes('outlet')) {
          tableColumns.push({
            key: 'outlet_name',
            label: i18n.t('articles.outlet'),
            orderable: true,
            filter: {
              title: i18n.t('articles.form.outlet.filter'),
              name: 'outlet.id',
              placeholder: i18n.t('articles.form.search'),
              searchOptions: this.searchOutletOptions,
              onFilter: (option) => self.onFilterTableHeader('outlet_name', option),
              onClearHeader: (value) => self.clearHeader ('outlet_name', value),
              applied: self.tableHeaderOutlet,
            }
          });
        }

        tableColumns.push({
          key: 'country',
          label: i18n.t('articles.country'),
          orderable: true,
        });

        tableColumns.push({
          key: 'language',
          label: 'Language',
          orderable: true,
        });

        tableColumns.push({
          key: 'comments',
          label: this.$i18n.tc('top_line.comment', 2),
          orderable: true,
          render: (row) => {
            if (row.comments)
              return (<i v-tooltip="Includes comment" class="fa fa-comment"></i>)

            return;
          }
        });

        if (allowedColumns.includes("date_created")) {
          tableColumns.push({
            key: 'created_at',
            label: i18n.t('articles.created_at'),
            orderable: true,
            render: (row) => {
              const date = Moment(row.created_at).utc().format("D MMM YYYY");
              return (<span>{date}</span>);
            }
            // input: {
            //   name: "created_at",
            //   type: FIELD_TYPE.CALENDAR,
            //   format: "d MMM yyyy",
            //   disabled: true,
            //   placeholder: ""
            // },
            // cellStyle: {
            //   "max-width": "130px"
            // }
          });
        }
      
        if (allowedColumns.includes('date_published')) {
          tableColumns.push({
            key: 'published_at',
            label: i18n.t('articles.published_at'),
            orderable: true,
            render: (row) => {
              const format = this.isProjectPage ? "D MMM YYYY HH:mm:ss" : "D MMM YYYY";
              const date = Moment.utc(row.published_at).format(format);
              return (<span>{date}</span>);
            }
            // input: {
            //   name: 'published_at',
            //   type: FIELD_TYPE.CALENDAR,
            //   format: "d MMM yyyy",
            //   useUtc: false,
            //   disabled: true,
            //   placeholder: ""
            // },
            // cellStyle: {
            //   "max-width": "130px"
            // }
          });
        }

        if (allowedColumns.includes("due_date")) {
          tableColumns.push({
            key: 'due_date',
            label: i18n.t('articles.due_date'),
            orderable: true,
            input: {
              name: "due_date",
              type: FIELD_TYPE.CALENDAR,
              format: "d MMM yyyy",
              minimum: new Date(),
              // maximum: new Date(2020, 0, 25),
              onChange: function(value, key, row) {
                self.updateRecord(value, key, row, true);
              }
            },
            cellStyle: {
              "max-width": "130px"
            }
          });
        }

        if (allowedColumns.includes('status') && article_states) {
          tableColumns.push({
            key: 'analysis_state',
            label: i18n.t('articles.status'),
            orderable: true,
            render: (row) => {
              const isUnassigned = !row.assigned_to.id;
              let tooltipMessage = isUnassigned
                ? self.$t("project.message.unassigned_cant_change_status")
                : '';
              let options = article_states.map((status) => {
                const isDisabled = 
                  (!this.project?.enable_auto_coder && 
                    (status.id === ARTICLE_STATUS.ERROR || status.id === ARTICLE_STATUS.QUEUED)) || 
                  (isUnassigned && 
                    (status.id === ARTICLE_STATUS.PUBLISHED_QC || 
                    status.id === ARTICLE_STATUS.PUBLISHED_NOT_QC));

                return {
                  value: status.id,
                  label: status.status,
                  $isDisabled: isDisabled,
                };
              });

              if (isUnassigned) {
                options = options.filter((option) => {
                  return !option.disabled;
                });
              }

              return (
                <InputSelect
                  class="datatable-input-select--fixed"
                  value={row.analysis_state}
                  searchable={true}
                  options={options}
                  allowEmpty={false}
                  v-tooltip={tooltipMessage}
                  placeholder={self.$t("project.form.select_state")}
                  onChange={(value) => {
                    self.updateRecord(value, "analysis_state", row);
                  }}
                />
              );
            },
            filter: {
              title: 'Filter by state',
              name: 'analysis_state',
              placeholder: 'Search',
              options: article_states.map(article_states => ({ value: article_states.id, label: article_states.status, operator: 'and' })),
              onFilter: (option) => self.onFilterTableHeader('status_id', option),
              onClearHeader: (value) => self.clearHeader ('status_id', value),
              applied: self.tableHeaderStatus,
            }
          })
        }

        if (allowedColumns.includes('outlet_tier') && outletTiers.length > 0) {
          tableColumns.push({
            key: 'outlet_tier',
            label: i18n.t('articles.outlet_tier'),
            orderable: true,
            input: {
              name: "outlet_tier",
              type: FIELD_TYPE.INPUT,
              styleNames: { border: "none", outline: 'none', color: '#444' },
              placeholder: "N / A",
              readOnly: true
            },
            filter: {
              title: 'Filter by outlet tier',
              name: 'outlet_tier',
              placeholder: 'Search',
              options: outletTiers.map(tiers => ({ value: tiers.type, label: tiers.name, operator: 'and' })),
              onFilter: (option) => self.onFilterTableHeader('outlet_tier', option),
              onClearHeader: (value) => self.clearHeader ('outlet_tier', value),
              applied: self.tableHeaderTier,
            }
          })
        }

        tableColumns.push({
          key: "insight_url",
          label: "Insight URL",
          render: (row) => {
            if (!row.coding_uid) return;
            const url = insightExternalUrl(row.coding_uid);
            return (
              <div class="url-cell">
                <a href={url} target="_blank" >{url}</a>
              </div>
            );
          },
          cellStyle: {
            "max-width": "190px"
          }
        });

        tableColumns.push({
          key: "source_url",
          label: "Source URL",
          render: (row) => {
            if (row.original_url) {
              return (
                <div class="url-cell">
                  <a href={row.original_url} target="_blank" >{row.original_url}</a>
                </div>
              );
            }
            return;
          },
          cellStyle: {
            "max-width": "190px"
          }
        });

        if (allowedColumns.includes('authors')) {
          tableColumns.push({
            key: 'authors',
            label: this.socialsDataEnabled ? i18n.t('articles.authors_handle') : i18n.t('articles.authors'),
            render: (row) => {
              return row.authors && row.authors.join(', ');
            }
          });
        }

        if (this.socialsDataEnabled) {
          tableColumns.push({
            key: 'user_country',
            label: i18n.t('articles.user_country'),
            orderable: true,
          });

          const socialColumns = [
            {
              key: "gender",
              label: this.$i18n.t('articles.gender'),
              render: (row) => {
                return row.gender && `${this.$i18n.t('gender.' + row.gender)}`
              }
            },
            {
              key: "sentiment",
              label: this.$i18n.t('articles.sentiment'),
              render: (row) => {
                return typeof row.sentiment === "number" && `${this.$i18n.t('sentiment.' + row.sentiment)}`;
              }
            },
            { key: "followers", label: this.$i18n.t('articles.followers') },
            { key: "likes", label: this.$i18n.t('articles.likes') },
            { key: "no_comments", label: this.$i18n.t('articles.no_comments') },
            { key: "shares", label: this.$i18n.t('articles.shares') },
            { key: "views", label: this.$i18n.t('articles.views') },
            { key: "reactions", label: this.$i18n.t('articles.reactions') }
          ];
          Array.prototype.push.apply(tableColumns, socialColumns);
        }

        if (allowedColumns.includes('relevancy') && this.relevancyEnabled) {
          tableColumns.push({
            key: 'analysis_relevancy',
            label: i18n.t('relevancy.form.auto_assign.title'),
            orderable: true,
            render: (row) => {
              if(row.media_subtype === 'social-networks' && (!this.isCarmaUser || !this.socialRelevancyEnabled) ){
                  return ' ';
                }
                let mapping = this.relevancy?.settings?.mapping || relevancyMappingDefaultLimit;
                if(row.relevancy_score >= mapping.high_upper_limit){
                  return <span>{i18n.t('relevancy.form.auto_assign.critical_limit')}</span>
                } else if (row.relevancy_score >= mapping.medium_upper_limit){
                  return <span>{i18n.t('relevancy.form.auto_assign.high_limit')}</span>
                } else if (row.relevancy_score >= mapping.low_upper_limit){
                  return <span>{i18n.t('relevancy.form.auto_assign.medium_limit')}</span>
                } else if(row.relevancy_score >=0 && row.relevancy_score !== null){
                  return <span>{i18n.t('relevancy.form.auto_assign.low_limit')}</span>
                }
              return <span>{i18n.t('relevancy.form.auto_assign.na')}</span>
            }
          })
        }
        
        const orderColumn = preferences.articles_table_column_order &&
          preferences.articles_table_column_order.order ?
          preferences.articles_table_column_order.order :
          self.pagination.order;

        const orderSort = preferences.articles_table_column_order &&
          preferences.articles_table_column_order.sort ?
          preferences.articles_table_column_order.sort :
          self.pagination.order;

        const hiddenColumn = preferences.articles_table_hidden_column &&
          preferences.articles_table_hidden_column.hidden ?
          preferences.articles_table_hidden_column.hidden :
          ["source_url", "insight_url", "other"];

        let widthColumn = preferences.articles_table_column_size &&
          preferences.articles_table_column_size.widths ?
          preferences.articles_table_column_size.widths :
          this.user.code_admin === false ? [] : [110, 350];

        const orderedTableColumns = Array.isArray(preferences?.articles_table_ordered_columns?.ordered_columns) && 
        preferences?.articles_table_ordered_columns?.ordered_columns?.length ? 
        preferences.articles_table_ordered_columns.ordered_columns : [];
        if(orderedTableColumns.length){
          tableColumns = tableColumns.sort((a, b) => orderedTableColumns.indexOf(a.key) - orderedTableColumns.indexOf(b.key));
        }
        
        this.tableData = {
          // Custom components that are not in FIELD_TYPE, and therefore unknown to the datatable, must be defined here.
          // Then the custom component should also be placed inside column.input.component
          custom_components: {
            "coding-headline": CodingHeadline,
            "outlets-picker": OutletsPicker
          },
          body,
          resizable: true,
          stickyHeader: true,
          columns: tableColumns,
          order: {
            column: orderColumn,
            direction: orderSort,
            onChange: function(column, direction) {
              self.onSortTable(column, direction);
            }
          },
          colReorder: {
            enabled: false,
            disabledColumnsLeft: 1
          },
          fixedColumns: {
            enabled: this.user.code_admin !== false,
            fixedColumnsLeft: this.user.code_admin === false ? 0 : 1,
            widths:  widthColumn
          },
          toggleColumns: {
            enabled: true,
            excluded: FIXED_TABLE_COLUMNS,
            hidden: hiddenColumn,
          },
          bulkActions: {
            enabled: bulkActionsEnabled,
            onSelect: function(rows){
              this.selectedRows = rows;
            }.bind(this)
          },
        };
      },
      deep: true
    },

    bulkEditOpen: {
      handler(value) {
        this.showErrorMessage = false;
        const { projectUid, selectedRows } = this;
        if (value === true) {
          this.getOrganizations({ projectId: projectUid });
          this.getCategories({ projectUid });
          this.getRelationshipLines({projectId: projectUid, selectedRows: selectedRows.map(article => article.id)});
        }
      }
    },
    'selectedRows': {
      handler(value) {
        if (value.length === 0) {
          this.actionTarget = this.actionTargetSelected;
        }
      }
    }
  },

  methods: {
    ...mapActions({
      getUserPreferences: 'user/getUserPreferences',
      getRelevancy : "relevancy/getRelevancy",
    }),
    updateDateRangeFilter (range) {
      this.isDateTimeUpdated = true;
      this.filters.dateFrom = this.formatDateParam(range[0]);
      this.filters.dateTo = this.formatDateParam(range[1]);
      
    },
    checkURLParams(params) {
      if (params.published_from) {
        params.date_from = params.published_from;
        delete params.published_from;
      }

      if (params.published_to) {
        params.date_to = params.published_to;
        delete params.published_to;
      }

      return params;
    },
    setFilterDate() {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.get('date_from')) {
        this.filters.dateFrom = this.formatDateParam(urlParams.get('date_from'));
      }

      if (urlParams.get('published_from')) {
        this.filters.dateFrom = this.formatDateParam(urlParams.get('published_from'));
      }

      if (urlParams.get('date_to')) {
        this.filters.dateTo = this.formatDateParam(urlParams.get('date_to'));
      }

      if (urlParams.get('published_to')) {
        this.filters.dateTo = this.formatDateParam(urlParams.get('published_to'));
      }

      if (urlParams.get('date_type')) {
        this.filters.dateType = urlParams.get('date_type');
      }

      this.pagination.order = this.filters.dateType === 'published_at'
        ? this.pagination.order = 'published_at'
        : this.pagination.order = 'created_at';
    },
    searchOutletOptions(search) {
      return Axios.get(`${API_HOST}/analysis/${this.$route.params.project_uid}/articles/outlets?per_page=20&page=1&order=name&sort=asc&name=${search}`, {
        headers: {
          Authorization: `Token token=${this.user.accessToken}`
        }
      }).then(res => {
        const outlets = [];
        if (res.data.data) {
          Array.prototype.push.apply(outlets, res.data.data.map(r => ({ value: r.name, label: r.name, operator: 'and' })));
        }
        return outlets;
      });
    },
    onReachEnd() {
      if(!this.fetchingArticles && this.tableData.stickyHeader && this.requestPagination.totalPages > this.pagination.page ) {
        this.pagination.page = parseInt(this.pagination.page) + 1;
        this.fetchArticles();
      }
    },
    exportArticles() {
      this.shownExportForm = true;
    },
    openOnEditor() {
      if(this.viewOnEditorEnabled) this.$router.push(`${this.viewEditorLink}`);
    },
    closeModalAction() {
      this.actionTarget = this.actionTargetSelected;
      this.clearSelected();
      this.refreshTable();
    },
    clearSelected(){
      this.selectedRows = [];
    },
    refreshTable() {
      this.dataTableArticleKey++;
    },
    articleCreated() {
      this.shownNewArticleForm = false;
      this.fetchArticles();
    },

    processRouteParams() {
      if (this.isAuthorArticlePage) {
        const pageAuthorId = this.$route.params.author_id;
        const filtersContainAuthor = this.filters.author_id.filter(a => a.value == pageAuthorId).length > 0;
        if (!filtersContainAuthor) {
          const author = this.authors.find(author => author.id === parseInt(pageAuthorId, 10));
          this.filters.author_id.push({ label: `${author.first_name}${author.last_name !== null ? ' ' + author.last_name : ''}`, value: pageAuthorId, operator: 'and', index: this.filters.author_id.length, locked: true });
        }
      }
      if (this.isGroupArticlePage) this.filters.groupId = this.$route.params.group_id
      if (this.isTagArticlePage) this.filters.tag_id = [
        {
          label: this.selectedTagName,
          value: this.categoryId
        }
      ];
      if (this.isSourceArticlePage) this.filters.sourceId = this.$route.params.source_id
      if (this.isOrganizationArticlePage) this.filters.organizationId = this.$route.params.organization_id
      if (this.isCoderArticlePage) {
        this.filters.coder_id[0] = {value: this.$route.params.coder_id, operator: "and"}
        this.getCoders({ projectId: this.$route.params.project_uid }).then(data => {
          this.isReadyCoders = true;
          const target = data.find(coder => coder.id === parseInt(this.$route.params.coder_id))
          this.$set(this.filters.coder_id[0], 'label', target.name);
          this.filterKey++;
        })
      }
      if (!this.unfilteredEnabled) {
        this.filters.unfiltered = 0;
      }
    },

    async fetch(filterParams) {
      this.processRouteParams();
      filterParams = filterParams ? filterParams : this.filters;

      await this.getUserPreferences({ name: 'articles_table_column_order' });
      await this.getUserPreferences({ name: 'articles_table_hidden_column' });
      await this.getUserPreferences({ name: 'articles_table_column_size' });
      await this.getUserPreferences({ name: 'articles_table_ordered_columns', projectId: this.projectUid });

      if (_.get(this.preferences, 'articles_table_column_order.order'))
        this.pagination.order = this.preferences.articles_table_column_order.order;

      if (_.get(this.preferences, 'articles_table_column_order.sort'))
        this.pagination.sort = this.preferences.articles_table_column_order.sort;

      this.getArticles({
        projectId: this.projectUid,
        pagination: this.pagination,
        filters: filterParams
      })
      .then(() => this.isReadyArticles = true)
    },

    toggleShowMoreArticle: function() {
      this.showMoreArticle = this.showMoreArticle === false ? true : false;
    },

    fetchArticles: function(forceCall = false) {
      const { projectUid, pagination } = this;

      this.fetchingArticles = true;

      if (!this.isCoderArticlePage) this.processRouteParams();
      const filters = _.cloneDeep(this.filters);
      const filterParams = this.sanitiFilterParams(_.cloneDeep(filters))
      return this.getArticles({ projectId: projectUid, pagination, filters: filterParams, forceCall }).then(params => {
        this.fetchingArticles = false;
        if(params) {
          const query = parseQuery(params)
          this.$router.push({query: query}).catch(err => err)
          this.queryInitialLoad = true
        }
      });
    },

    fetchOutlets: function(search) {
      const projectId = this.projectUid;
      const pagination = {
        page: 1,
        perPage: 20,
        order: 'name',
        sort: 'asc'
      }
      const mediaType = this.filters.media_type.map((m) => m.value);
      return this.getOutlets({ projectId, pagination, filterValues: { mediaType, name: search } }).then(res => res.data.data);
    },

    fetchCoders: function(search) {
      const projectId = this.projectUid;
      const pagination = {
        page: 1,
        perPage: 20,
        order: 'name',
        sort: 'asc'
      }
      return this.getCoders({ projectId, pagination, filters: { ...this.filters, name: search } }).then(res => res);
    },

    fetchCountries: function(search) {
      const projectUid = this.projectUid;
      const pagination = {
        page: 1,
        perPage: 20,
        order: 'name',
        sort: 'asc'
      }
      return this.getCountries({ projectUid, pagination, filters: { ...this.filters, name: search } }).then(res => res.data.data);
    },

    fetchNextArticle: function(article) {
      if(_.isEmpty(article) && !this.fetchingArticles && this.requestPagination.currentPage < this.requestPagination.totalPages) {
        this.articlePreviewIsReady = false;
        this.pagination.page = parseInt(this.pagination.page) + 1;
        this.fetchArticles().then(r => {
          let newIndex = this.articles.findIndex(a => _.isEqual(a, this.sidebarPagination.currentArticle)) + 1;
          this.openArticlePreview(this.articles[newIndex]);
        });
      }
      else if(article && article.id) {
        this.openArticlePreview(article);
      }
    },

    openArticlePreview: function(article) {
      this.articlePreviewIsReady = false;
      this.articlePreviewContent = {};
      this.articlePreviewOpen = true;

      const id = article.id;
      this.sidebarPagination.currentArticle = article;
      this.fetchArticlePreview(id).then(r => {
        const content = {
          ...this.articles.find(a => a.id === id),
          ...this.article
        }
        this.articlePreviewContent = content;

        //Set article preview pagination
        const articleIndex = this.articles.findIndex(a => _.isEqual(a, article));
        this.sidebarPagination.previousArticle = articleIndex > 0 ? this.articles[articleIndex-1] : {};
        this.sidebarPagination.nextArticle =  articleIndex < this.articles.length-1 ? this.articles[articleIndex + 1] : {};

        this.articlePreviewIsReady = true;
      });
    },

    fetchArticlePreview: function(id) {
      const { projectUid } = this;
      const cancelToken = Axios.CancelToken.source();
      return Promise.all([
        this.getArticle({
          projectId: projectUid,
          articleId: id,
          cancelToken
        }),
        //this.getMentions({ projectId: projectUid, articleId: id }),
        this.getArticleAsset({ projectId: projectUid, articleId: id }),
        this.getRelationshipLines({projectId: projectUid, selectedRows: [id]}),
        this.getSettings({projectId: projectUid})
      ]);
    },

    onBulk: function(action) {
      if (action == "group") {
        this.bulkGroupOpen = true;
      }

      if (action == "edit") {
        this.bulkEditOpen = true;
      }

      if (action == "remove") {
        this.bulkRemoveFromGroupOpen = true;
      }

      if (action == "delete") {
        this.bulkDeleteOpen = true;
      }

      if (action == "ai_code") {
        this.bulkAICode = true;
      }
    },

    onBulkOpen() {
      this.selectedRows.forEach(article => {
        window.open(article.original_url, '_blank');
      });
    },

    onBulkOption(val){
      this.actionTarget = val;
    },

    onSubmitArticlesToGroup: function(articles, groupId) {
      const { projectUid } = this;

      this.groupArticles({
        projectUid,
        articles,
        groupId,
        toAllRecords: this.actionToAllRecordsData
      })
        .then(() => {
          this.closeSidePanel();
          this.clearSelected();
          this.$refs.table.onSelectAllChange(false);
        });
    },

    onSubmitArticlesToDelete: function(articles) {
      const { projectUid } = this;
      if (this.isGroupArticlePage) {
        this.deleteArticlesFromGroup({
          projectId: projectUid,
          articleIds: articles.map((a) => a.id),
          groupId: this.$route.params.group_id
        }).then(() => {
          this.closeSidePanel();
          this.clearSelected();
        });
      } else {
        this.deleteArticles({
          projectUid,
          articles,
          toAllRecords: this.actionToAllRecordsData
        }).then(() => {
          this.closeSidePanel();
          this.clearSelected();
        });
      }
    },

    onSubmitEditArticles: function(changes, forceUpdate = false) {
      this.fetchingBulkUpdate = true;
      const { projectUid } = this;
      const articleIds = this.selectedRows.map(a => a.id);
      this.newModel = changes;
      this.updateArticles({
        projectUid,
        articleIds,
        changes,
        forceUpdate: forceUpdate,
        toAllRecords: this.actionToAllRecordsData
      })
        .then(() => {
          this.closeSidePanel();
          this.clearSelected();
          this.fetchingBulkUpdate = false;
          // this.refreshTable();
          // this.isReady = false
          // const filterParams = filterParams ? filterParams : this.filters;
          this.getAllPagesItems();
        })
        .catch(error => {
          if (error.status === 400) {
            this.error = error;
            this.showErrorMessage = true;
          }
        });
    },

    onAICodeArticles: function (promptModel) {
      let filterParams = this.sanitiFilterParams(_.cloneDeep(this.filters));
      let filters, articleIds;
      if (this.actionToAllRecordsStatus) {
        filters = this.filters;
      } else {
        articleIds = this.selectedRows.map(a => a.id);
      }

      this.aiCodeArticles({ projectId: this.projectUid, articleIds, model_ids: promptModel?.model_ids, filters, priority_queue: promptModel?.priority_queue }).then(r => {
        if (r) {
          this.getArticles({
            projectId: this.projectUid,
            pagination: this.pagination,
            filters: filterParams,
            getAll: true
          }).then(() => {
            this.fetchingArticles = false;
          });
          this.closeSidePanel();
          this.clearSelected();
        }
      });
    },

    onSubmitEditArticlesConfirmForce() {
      this.onSubmitEditArticles(this.newModel, true)
    },

    onCancelConfirmForceUpdate () {
      this.showErrorMessage = false;
    },

    getAllPagesItems () {
      this.fetchingArticles = true;

      this.getArticles({
        projectId: this.projectUid,
        pagination: this.pagination,
        filters: this.filters,
        getAll: true
      }).then(() => {
        this.fetchingArticles = false;
      })
    },

    closeSidePanel: function() {
      this.articlePreviewOpen = false;
      this.bulkGroupOpen = false;
      this.bulkEditOpen = false;
      this.bulkDeleteOpen = false;
      this.bulkRemoveFromGroupOpen = false;
      this.bulkAICode = false;
      this.showMoreArticle = false;
      this.shownNewArticleForm = false;
      this.shownExportForm = false;
    },

    onSearch: function(value) {
      // console.log('onSearch', value);
      const { projectUid, findResultsRequest } = this;

      if (findResultsRequest) {
        findResultsRequest.cancel('aborted');
      }

      const request = Axios.CancelToken.source();
      this.getFindResults({
        projectUid: this.projectUid,
        query: value,
        requestSource: request,
        filters: this.filters
      });
    },

    onSearchSubmit: function(params) {
      if(!params) return this.resetFilter()
      this.filtering = true;
      this.filters = { ...this.filters, ..._.cloneDeep(this.searchBarFilters) };

      if (_.isArray(params) && params.length) params[0].operator = 'and';

      const routeName = this.$route.name;
      if (routeName == 'coder-articles'){
        if(JSON.stringify(this.filters.coder_id) == '[]') {
          let coder = [{value: this.$route.params.coder_id, operator: "and"}];
          this.filters.coder_id = coder; // shouldn't be pushed?
        }
      }
      this.pagination.page = 1;
      this.fetchArticles().then(r => {
        this.filtering = false;
      });
    },

    sanitiFilterParams(filterParams) {
      Object.keys(filterParams).forEach(key => {
        if(Array.isArray(filterParams[key])) {
          filterParams[key].forEach(filter => {
            delete filter.id
            delete filter.type
          })
        }
      })
      return filterParams
    },

    resetFilter() {
      this.filters = {
        ...this.filters,
        groupId : "",
        tag_id : [],
        organizationId: "",
        status_id : [],
        coder_id: this.isCoderArticlePage ? this.filters.coder_id : [],
        country_code : [],
        keyword : [],
        text_in_outlet : [],
        text_in_headline : [],
        outletId : [],
        outlet_name : [],
        media_type : [],
        outlet_tier : [],
        article_id : [],
        user_country_code: [],
        insight_tag_id: [],
        languages: [],
        source_url: [],
        insight_url: [],
        coding_uid: [],
        relevancy: [],
        author_id: [],
        social: []
      }

      this.pagination.page = 1;
      this.fetchArticles().then(r => {
        this.filtering = false;
      });
    },

    onNewTagCreateGroup: function (value) {
      const {projectUid, user} = this;
      let payload = {name: value, userId: user.id};
      return this.createGroup({projectUid, payload});
    },

    deleteMention: function (mention){
      const { projectUid } = this;
      const articleIds = this.selectedRows.map(a => a.id);
      this.bulkDeleteMention({projectId:projectUid,articleIds,mention});
    },

    onSortTable: function(column, direction) {
      this.pagination.order = column;
      this.pagination.sort = direction;
      this.pagination.page = 1;
      this.fetchArticles();
    },

    previewUpdateStatus() {
      const row = this.articles.find(item => {
        return item.id === this.articlePreviewContent.id
      });

      this.updateRecord(
        this.articlePreviewContent.analysis_state,
        'analysis_state',
        row
      )
    },

    updateRecord: function(value, key, row, preUpdate = false) {
      const record = {
        ...row,
        [key]: value
      };
      const { projectUid } = this;
      if (!preUpdate) this.updatingRowId.push(row.id);

      if (key.trim().toLowerCase() === 'analysis_state' && value == 1 && this.filters.unfiltered === 0) {
        this.updateArticle({ projectUid, record, preUpdate })
          .then( () => {
            this.fetchArticles();
          })
          .finally(() => {
            this.clearSelected();
            const i = this.updatingRowId.indexOf(record.id);
            if (i > -1){
              this.updatingRowId.splice(i, 1);
            } 
          });
      } else {
        this.updateArticle({ projectUid, record, preUpdate })
          .catch(() => {
            if(Array.isArray(this.updatingRowId) && !this.updatingRowId.length) return
            this.refreshTable();
          })
          .finally(() => {
            this.clearSelected();
            const i = this.updatingRowId.indexOf(record.id);
            if (i > -1){
              this.updatingRowId.splice(i, 1);
            } 
          });
      }
    },

    resetTableHeaderFilters: function() {
      Object.keys(this.tableHeaderFilters).forEach(key => this.filters[key] = [])
    },

    clearHeader: function (filter) {
      this.filters[filter] = [];
      this.pagination.page = 1;
      this.fetchArticles();
    },

    onChangeUnfiltered: function(value) {
      this.pagination.page = 1;
      this.filters.unfiltered = value == true ? 1 : 0;
      this.resetTableHeaderFilters();
      this.fetchArticles();
    },

    onChangeGroupFilter: function(value){
      this.pagination.page = 1;
      this.filters.groupId = value;
      if(value == null) {
        this.filters.groupId = ""
      }
      this.fetchArticles();
    },

    onChangeDateFilters: function (
      { dateType, dateFrom, dateTo } = {
        dateType: this.filters.dateType,
        dateFrom: this.filters.dateFrom,
        dateTo: this.filters.dateTo,
      }
    ) {

      if (
        this.pagination.order === ARTICLES.FILTERS.CREATED_AT &&
        dateType === ARTICLES.FILTERS.PUBLISHED_AT
      ) {
        this.pagination.order = ARTICLES.FILTERS.PUBLISHED_AT;
      } else if (
        this.pagination.order === ARTICLES.FILTERS.PUBLISHED_AT &&
        dateType === ARTICLES.FILTERS.CREATED_AT
      ) {
        this.pagination.order = ARTICLES.FILTERS.CREATED_AT;
      }

      this.pagination.page = 1;            
      this.fetchArticles(true);
    },

    onFilterTableHeader: function(key, option) {
      if (option) {
        this.filters[key] = []
        this.filters[key].push(option)
        this.pagination.page = 1;
        this.fetchArticles();
      }
    },

    onChangeCoder: function(coder, row) {
      let changedRow = Object.assign({}, row, { assigned_to: { ... row.assigned_to, id: coder.value } });
      changedRow.assigned_to.id = coder.value;
      changedRow.assigned_to.name = coder.label;
      this.updateRecord(coder.value, "coder_id", changedRow, true);
    },

    scroll: function() {
      window.onscroll = () => {
        const { currentPage, totalPages } = this.requestPagination;

        if (currentPage < totalPages && this.$refs.table && !this.stickyHeader) {
          const wHeight = window.innerHeight;
          const {
            top,
            bottom,
            height
          } = this.$refs.table.$el.getBoundingClientRect();
          const margin = 100;

          if (top * -1 + wHeight >= height - margin && this.isBusy == false && this.checkSidepanelOpen == false) {
            if(!this.fetchingArticles && !this.tableData.stickyHeader) {
              this.pagination.page = parseInt(this.pagination.page) + 1;
              this.fetchArticles();
            }
          }
        }
      };
    },

    insightUrl: function(codingUid) {
      return insightExternalUrl(codingUid);
    },

    getTag: function (tagId) {
      return Axios.get(`${API_HOST}/analysis/${this.$route.params.project_uid}/tags/tag/${tagId}`, {
        headers: {
          Authorization: `Token token=${this.user.accessToken}`
        }
      }).then(res => {
        this.selectedTag = res.data;
      });
    },

    formatDateParam: function (date, forceTime) {
      if (!date) return null;
      const format = forceTime || this.isProjectPage ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD';
      return Moment.utc(date).format(format);
    },


    handleAiCodingPrev() {
      this.aiCodingIndex -= 1;
    },

    handleAiCodingNext() {
      this.aiCodingIndex += 1;
    },

    ...mapActions({
      getArticles: "articles/getArticles",
      getArticlesByTag: "articles/getArticlesByTag",
      getArticlesByAuthor: "articles/getArticlesByAuthor",
      getAuthors: 'admin/authors/getAuthors',
      getMoreArticles: "articles/getMoreArticles",
      searchArticles: "articles/search",
      updateArticle: "articles/update",
      groupArticles: "articles/group",
      deleteArticles: "articles/delete",
      deleteArticlesFromGroup: "articles/deleteBulkArticlesFromGroup",
      updateArticles: "articles/updateBulk",
      getArticleStatus: "articles/getArticleStatus",
      getCoders: "articles/getCoders",
      getFindResults: "articles/getFindResults",
      createGroup: "articles/createGroup",
      getGroup: "articleGroup/getGroup",
      getMentions: "articles/getMentions",
      getArticleAsset: "articles/getArticleAsset",
      getOutlets: "admin/outlets/getOutlets",
      getCountries: "countries/getCountries",
      getOrganizations: "articles/getOrganizations",
      getCategories: "articles/getCategories",
      getRelationshipLines: "articles/getRelationshipLines",
      bulkDeleteMention: "articles/bulkDeleteMention",
      getArticle: "articles/getArticle",
      getSettings: "articles/getSettings",
      aiCodeArticles: "articles/aiCodeArticles"
    })
  }
};
</script>

<style lang="scss" scoped>
@import "~@/styles/components/coding-admin/tags";

.container {
  display: block;

  :deep(table.table.table--datatable) {
    tbody td {
      .cell-content {
        font-size: 12px;
      }
    }
  }

  .search-area {
    z-index: 5;
  }

  .bulk-actions {
    display: block;

    &__option {
      display: flex;
      align-items: center;

      input {
        margin-right: 10px;
      }
    }

    .actions {
      margin-top: 20px;
      display: flex;
      justify-content: space-between;
    }

  }

  @import "~@/styles/components/loader";

  .table-top {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
  }

  .toggle-unfiltered {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 5px;

    & > span {
      font-size: 13px;
      margin-left: 10px;
    }
  }

  .articles-wrapper {
    position: relative;
    min-height: 100px;
  }

  :deep(.article-sidepanel) {
    .editor-ui-link {
      display: inline-block;
      margin: 20px 0;
    }
  }

}

.invisible {
  width: 1%;
  height: 1%;
  color: transparent;
}

.filters-articles-list {
  display: flex;
}

.mention-wrapper{
  margin-top: 40px;
}
.preview-select-status {
  display: inline-block;
  margin-left: 10px;
}

</style>

